// @ts-strict-ignore
import React, { useEffect } from 'react'
import styled from 'styled-components/native'

import Analytics from 'APP/Services/Analytics'
import I18n from 'APP/I18n'
import { usePushNotificationPermission } from 'APP/Hooks/usePushNotificationPermission'

import BulletListItem from 'APP/Components/BulletListItem'
import LogoHeader from 'APP/Components/LogoHeader'
import List from 'APP/Components/List'
import Typography from 'APP/Converse/Typography'
import Button from 'APP/Converse/Button'
import { ResponsiveView } from 'APP/Converse/Layout'

const ContentContainer = styled(ResponsiveView)`
  gap: ${({ theme }) => theme.metrics.baseMargin * 2}px;
`

const InnerContentContainer = styled.View`
  gap: ${({ theme }) => theme.metrics.baseMargin}px;
`

const CTA_TITLE_KEYS = {
  enable: 'NotificationsScreen.notificationsNotAllowed.enableCta',
  openSettings: 'NotificationsScreen.notificationsNotAllowed.deviceSettingsCta',
} as const

const CTA_ANALYTICS_NAMES = {
  enable: 'NotificationsScreen.enableNotifications',
  openSettings: 'NotificationsScreen.goToDeviceSettings',
} as const

interface NotificationsNotAllowedProps {
  onPermissionsChanged?: () => void
}

export const NotificationsNotAllowed = ({
  onPermissionsChanged,
}: NotificationsNotAllowedProps = {}) => {
  const { canPromptForPermission, requestPermissionOrOpenSettings } = usePushNotificationPermission(
    {
      onPermissionsChanged,
      source: 'NotificationsNotAllowed',
    }
  )

  const [bulletPointTitle, ...bulletPoints] = I18n.t(
    'NotificationsScreen.notificationsNotAllowed.bulletPoints',
    {
      returnObjects: true,
    }
  ) as string[]

  useEffect(() => {
    Analytics.trackScreen('ENABLE PUSH')
  }, [])

  const ctaTitle = I18n.t(
    canPromptForPermission ? CTA_TITLE_KEYS.enable : CTA_TITLE_KEYS.openSettings
  )

  const ctaAnalyticsName = canPromptForPermission
    ? CTA_ANALYTICS_NAMES.enable
    : CTA_ANALYTICS_NAMES.openSettings

  return (
    <ContentContainer>
      <LogoHeader
        title={I18n.t('NotificationsScreen.title')}
        titleVariant="h2"
        subtitle={
          <Typography variant="bodySmall">
            {I18n.t('NotificationsScreen.notificationsNotAllowed.body')}
          </Typography>
        }
      />
      <InnerContentContainer>
        <Typography variant="subheader">{bulletPointTitle}</Typography>
        <List>
          {bulletPoints.map((text, idx) => (
            <BulletListItem key={`notifications-not-allowed-bp-${idx}`}>
              <Typography>{text}</Typography>
            </BulletListItem>
          ))}
        </List>
      </InnerContentContainer>
      <Button
        variant="primary"
        widthVariant="full"
        title={ctaTitle!}
        analyticsName={ctaAnalyticsName}
        testID="NOTIFICATIONS_NOT_ALLOWED_BTN"
        onPress={requestPermissionOrOpenSettings}
      />
    </ContentContainer>
  )
}
